import { ref, get, update, onValue, remove } from 'firebase/database';
import { database } from '../config/firebase';
import { TemplateElement, MoodBoardTemplate } from './templateService';
import { storage } from '../config/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ref as dbRef, set as dbSet } from 'firebase/database';
import activityService from './activityService';

export interface DesignDocument {
  id: string;
  owner_uid: string;
  base_template_id: string;
  name: string;
  preview_image_url: string;
  elements: TemplateElement[];
  collaborators?: Record<string, { displayName: string; color: string }>;
  /** Optional team association – if set, all members of the team should be able to view & edit */
  team_id?: string;
  last_modified: number;
}

// Create a personal document by forking an existing default template
export const forkTemplateToDocument = async (
  template: MoodBoardTemplate,
  ownerUid: string,
  teamId?: string
): Promise<string> => {
  const docId = `doc_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  const docRef = dbRef(database, `documents/${docId}`);
  const payload = {
    owner_uid: ownerUid,
    base_template_id: template.id,
    name: template.name,
    preview_image_url: template.preview_image_url,
    elements: template.elements || [],
    ...(teamId ? { team_id: teamId } : {}),
    last_modified: Date.now(),
  };
  console.log('Creating document with payload:', payload);
  await dbSet(docRef, payload);
  console.log('Document created successfully with ID:', docId);
  
  // Log activity for design creation
  await activityService.logDesignCreated(ownerUid, docId, template.name);
  
  return docId;
};

export const fetchDocumentById = async (docId: string): Promise<DesignDocument | null> => {
  const docRef = dbRef(database, `documents/${docId}`);
  const snapshot = await get(docRef);
  if (!snapshot.exists()) return null;
  return { id: docId, ...(snapshot.val() as Omit<DesignDocument, 'id'>) };
};

export const updateDocument = async (
  docId: string,
  updates: Partial<Omit<DesignDocument, 'id' | 'owner_uid' | 'base_template_id'>>,
  ownerUid?: string
) => {
  const docRef = dbRef(database, `documents/${docId}`);
  const updatePayload = { ...updates, last_modified: Date.now() };
  console.log('Updating document:', docId, 'with payload:', updatePayload);
  await update(docRef, updatePayload);
  console.log('Document updated successfully:', docId);
  
  // Log activity for design update if ownerUid is provided
  if (ownerUid && updates.name) {
    await activityService.logDesignUpdated(ownerUid, docId, updates.name);
  }
};

// Delete a personal design document
export const deleteDocument = async (docId: string) => {
  const docRef = dbRef(database, `documents/${docId}`);
  await remove(docRef);
};

// Listen to changes in a specific user's documents list
export const listenToUserDocuments = (
  ownerUid: string,
  callback: (docs: DesignDocument[]) => void
) => {
  const docsRef = dbRef(database, 'documents');
  return onValue(docsRef, (snapshot) => {
    const data = snapshot.val();
    console.log('Raw documents data:', data);
    if (!data) {
      console.log('No documents found in database');
      callback([]);
      return;
    }
    const list: DesignDocument[] = Object.entries(data)
      .map(([id, doc]) => ({ id, ...(doc as any) }))
      .filter((doc) => doc.owner_uid === ownerUid);
    console.log('Filtered documents for user', ownerUid, ':', list.length);
    console.log('Filtered documents:', list.map(d => ({ id: d.id, name: d.name, owner_uid: d.owner_uid })));
    callback(list);
  });
};

// Listen to all documents the user has access to (personal + team docs)
export const listenToAccessibleDocuments = (
  ownerUid: string,
  teamIds: string[],
  callback: (docs: DesignDocument[]) => void
) => {
  const docsRef = dbRef(database, 'documents');
  return onValue(docsRef, (snapshot) => {
    const data = snapshot.val();
    if (!data) {
      callback([]);
      return;
    }
    const list: DesignDocument[] = Object.entries(data)
      .map(([id, doc]) => ({ id, ...(doc as any) }))
      .filter((doc) =>
        doc.owner_uid === ownerUid ||
        (doc.team_id && teamIds.includes(doc.team_id))
      );
    callback(list);
  });
};

// Listen to elements array for a design document
export const listenToElements = (
  docId: string,
  callback: (elements: TemplateElement[]) => void
) => {
  const refEl = dbRef(database, `documents/${docId}/elements`);
  return onValue(refEl, (snap) => {
    const data = snap.val();
    if (Array.isArray(data)) callback(data as TemplateElement[]);
  });
};

// Replace entire elements array (debounced by caller)
export const setElementsArray = async (docId: string, elements: TemplateElement[]) => {
  const elRef = dbRef(database, `documents/${docId}/elements`);
  await dbSet(elRef, elements);
};

export const uploadFileAndGetURL = async (file: File, path: string): Promise<string> => {
  const sRef = storageRef(storage, path);
  await uploadBytes(sRef, file);
  return await getDownloadURL(sRef);
}; 